'use client';

import { Fragment } from 'react';
import type { Route } from 'next';
import Link from 'next/link';
import { styled } from '@mui/material/styles';
import { COLORS, FONTS } from 'components/HomepageV3/theme/tokens';
import { withSe3AssetPrefix } from 'components/HomepageV3/assetPrefix';
import { useSe3QaSearch, withSe3QaOverride } from 'components/HomepageV3/utils/routes';

/**
 * PastOfferingsTicker — `.se-past-bar` / `.se-scroller-track`.
 *
 * Infinite horizontal marquee of companies previously featured on the platform.
 * Each item is a white logo (or serif name) with an optional mono IPO chip and a
 * diamond divider between items. The track is duplicated and translated -50% so
 * the loop is seamless. Motion is pure CSS keyframes and is disabled under
 * `prefers-reduced-motion: reduce`. Edge fade uses a mask gradient.
 *
 * Source: design-refs/source/assets/private-home.css (`.se-past-*`,
 * `.se-scroller-track`, `@keyframes scroll`) + private-home.js `tickerItems()`.
 */

export interface PastOffering {
  /** Company display name (also used as logo alt text). */
  name: string;
  /** White-logo asset path under /homepage-v3/logos/white/. */
  logo?: string;
  /** Rendered logo height in px (preserves the source per-logo sizing). */
  logoHeight?: number;
  /** Renders the mono "IPO ↗" chip. */
  ipo?: boolean;
  /** Curated current or historical StartEngine offering destination. */
  href?: string;
}

export interface PastOfferingsTickerProps {
  /** Label above the rail. Defaults to the editorial copy. */
  label?: string | null;
  /** Disclosure line below the rail. */
  disclosure?: string | null;
  /** Footnote marker rendered before the disclosure, tying it to the hero `¹`. */
  footnoteMarker?: string | null;
  /** Override the offerings list (otherwise the source ticker list is used). */
  items?: PastOffering[];
  className?: string;
  'data-cy'?: string;
}

/** Ported verbatim from private-home.js `tickerItems()`. */
export const PAST_OFFERINGS: PastOffering[] = [
  {
    name: 'Anthropic',
    logo: '/homepage-v3/logos/white/anthropic.png',
    logoHeight: 17,
  },
  {
    name: 'Kraken',
    logo: '/homepage-v3/logos/white/kraken.png',
    logoHeight: 24,
  },
  {
    name: 'Groq',
    logo: '/homepage-v3/logos/white/groq.png',
    logoHeight: 28,
  },
  {
    name: 'Stripe',
    logo: '/homepage-v3/logos/white/stripe.png',
    logoHeight: 26,
  },
  {
    name: 'Cerebras',
    logo: '/homepage-v3/logos/white/cerebras.png',
    logoHeight: 30,
  },
  {
    name: 'xAI',
    logo: '/homepage-v3/logos/white/xai.png',
    logoHeight: 30,
  },
  {
    name: 'Perplexity',
    logo: '/homepage-v3/logos/white/perplexity.png',
    logoHeight: 21,
  },
  {
    name: 'Discord',
    logo: '/homepage-v3/logos/white/discord.png',
    logoHeight: 22,
  },
  {
    name: 'Polymarket',
    logo: '/homepage-v3/logos/white/polymarket.png',
    logoHeight: 21,
  },
  {
    name: 'Crusoe',
    logo: '/homepage-v3/logos/white/crusoe.png',
    logoHeight: 22,
  },
];

const DEFAULT_DISCLOSURE =
  'Membership interests in Reg. D 506(c) offerings via StartEngine Primary, LLC. You are not buying portfolio company securities directly; portfolio companies are not a party to or endorser of these offerings. Names and logos are used solely to identify offerings.';

/* `.se-past-bar` — flat dark band; separated by a hairline, no gradient. */
const Bar = styled('section')({
  background: '#030D0C',
  borderTop: '1px solid rgba(233,215,182,0.12)',
  padding: '18px 0 20px',
  overflow: 'hidden',
  '@media (max-width: 768px)': {
    padding: '22px 0',
  },
});

/* `.se-past-label` */
const Label = styled('p')({
  margin: 0,
  fontFamily: FONTS.sans,
  fontSize: '10.5px',
  fontWeight: 500,
  letterSpacing: '0.36em',
  textTransform: 'uppercase',
  color: COLORS.onDark3,
  textAlign: 'center',
  marginBottom: '16px',
});

/* `.se-past-rail` — edge-fade mask. */
const Rail = styled('div')({
  overflow: 'hidden',
  WebkitMaskImage: 'linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent)',
  maskImage: 'linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent)',
});

/* `.se-scroller-track` + `@keyframes scroll` (60s linear infinite, -50%). */
const Track = styled('div')({
  display: 'flex',
  gap: '40px',
  alignItems: 'center',
  width: 'max-content',
  padding: '0 40px',
  // Slowed from 32s to 60s — the marquee scrolled too fast (QA).
  animation: 'se-past-scroll 60s linear infinite',
  '@keyframes se-past-scroll': {
    from: { transform: 'translateX(0)' },
    to: { transform: 'translateX(-50%)' },
  },
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
  },
  '@media (max-width: 768px)': {
    gap: '26px',
  },
});

/* `.se-past-item` */
const Item = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  whiteSpace: 'nowrap',
  flexShrink: 0,
  opacity: 0.7,
  transition: 'opacity 0.15s',
  '&:hover': { opacity: 1 },
});

const ItemLink = styled(Link)({
  color: 'inherit',
  textDecoration: 'none',
  borderRadius: '2px',
  '&:focus-visible': {
    outline: `2px solid ${COLORS.champagne}`,
    outlineOffset: '5px',
  },
});

/* `.se-past-name` */
const Name = styled('span')({
  fontFamily: FONTS.serif,
  fontSize: '19px',
  fontWeight: 400,
  color: '#fff',
  letterSpacing: '0.005em',
  '@media (max-width: 768px)': { fontSize: '16px' },
});

/* `.se-past-logo` */
const Logo = styled('img')({
  width: 'auto',
  display: 'block',
  opacity: 0.92,
});

/* `.se-past-ipo` */
const IpoChip = styled('span')({
  fontFamily: FONTS.mono,
  fontSize: '9px',
  fontWeight: 600,
  letterSpacing: '0.06em',
  color: COLORS.champagne,
  border: '1px solid rgba(233,215,182,0.4)',
  padding: '2px 7px',
  borderRadius: '3px',
});

/* `.se-past-divider` */
const Divider = styled('span')({
  width: '4px',
  height: '4px',
  borderRadius: '50%',
  background: COLORS.gold,
  flexShrink: 0,
  transform: 'rotate(45deg)',
});

/* `.se-past-disc` */
const Disclosure = styled('p')({
  fontFamily: FONTS.sans,
  fontSize: '15px',
  lineHeight: 1.6,
  color: COLORS.onDark2,
  textAlign: 'center',
  maxWidth: '1180px',
  margin: '0 auto 20px',
  padding: '0 var(--pad)',
  textWrap: 'pretty',
});

/* Footnote marker tying the disclosure back to the hero title's `¹`. */
const DiscMarker = styled('sup')({
  fontFamily: FONTS.sans,
  fontSize: '0.72em',
  fontWeight: 600,
  color: COLORS.gold,
  verticalAlign: 'super',
  marginRight: '5px',
  lineHeight: 0,
});

function ItemContent({
  offering,
  href,
  duplicate,
}: {
  offering: PastOffering;
  href?: Route;
  duplicate: boolean;
}) {
  const content = (
    <Item className="se-past-item" data-cy="past-offering-item">
      {offering.logo ? (
        <Logo
          className="se-past-logo"
          src={withSe3AssetPrefix(offering.logo)}
          alt={offering.name}
          style={{ height: `${offering.logoHeight ?? 22}px` }}
        />
      ) : (
        <Name className="se-past-name">{offering.name}</Name>
      )}
      {offering.ipo && (
        <IpoChip className="se-past-ipo" data-cy="past-offering-ipo">
          IPO ↗
        </IpoChip>
      )}
    </Item>
  );

  return href ? (
    <ItemLink
      href={href}
      aria-label={`View ${offering.name} offering`}
      aria-hidden={duplicate ? 'true' : undefined}
      tabIndex={duplicate ? -1 : undefined}
      data-cy={duplicate ? undefined : `past-offering-link-${offering.name.toLowerCase()}`}
    >
      {content}
    </ItemLink>
  ) : (
    content
  );
}

export function PastOfferingsTicker({
  label = null,
  disclosure = DEFAULT_DISCLOSURE,
  footnoteMarker = '1',
  items = PAST_OFFERINGS,
  className,
  'data-cy': dataCy = 'past-offerings-ticker',
}: PastOfferingsTickerProps) {
  const currentSearch = useSe3QaSearch();
  // Duplicate the track so the -50% translate produces a seamless loop.
  const sequence = [...items, ...items];

  return (
    <Bar
      className={className}
      data-cy={dataCy}
      aria-label="Past offerings featured on the platform"
    >
      {label ? <Label className="se-past-label">{label}</Label> : null}
      {disclosure && (
        <Disclosure className="se-past-disc" data-cy="past-offerings-disclosure">
          {footnoteMarker ? (
            <DiscMarker data-cy="past-offerings-disclosure-marker">{footnoteMarker}</DiscMarker>
          ) : null}
          {disclosure}
        </Disclosure>
      )}
      <Rail className="se-past-rail">
        <Track className="se-scroller-track" data-cy="past-offerings-track">
          {sequence.map((offering, index) => {
            const duplicate = index >= items.length;
            return (
              <Fragment key={`${offering.name}-${index}`}>
                <ItemContent
                  offering={offering}
                  href={
                    offering.href
                      ? (withSe3QaOverride(offering.href, currentSearch) as Route)
                      : undefined
                  }
                  duplicate={duplicate}
                />
                <Divider className="se-past-divider" aria-hidden="true" />
              </Fragment>
            );
          })}
        </Track>
      </Rail>
    </Bar>
  );
}

export default PastOfferingsTicker;
